
import React, { useState, useEffect } from "react";
import { MapFormProvider, useMapForm } from "../../context/MapFormContext";
import MapComponent from "../MapComponent";
import MultiCropFormComponent from "../MultiCropFormComponent";
import IrrigationScenarioSelector from "../IrrigationScenarioSelector";
import CropSummaryCards from "../CropSummaryCards";
import ThreeChartWaterDisplay from "../charts/CombinedWaterByStages";
import CombinedCropCoefficient from "../charts/CombinedCropCoefficient";
import Header from "../header/Header";

const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
    isVertical: window.innerHeight > window.innerWidth,
    isMobile: window.innerWidth < 768,
  });

  useEffect(() => {
    const handleResize = () => {
      const newScreenSize = {
        width: window.innerWidth,
        height: window.innerHeight,
        isVertical: window.innerHeight > window.innerWidth,
        isMobile: window.innerWidth < 768,
      };

      setScreenSize(newScreenSize);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return screenSize;
};

const useResizableLayout = (screenSize, rightPanelVisible) => {
  const [manualLeftWidth, setManualLeftWidth] = useState(null);
  const [manualRightWidth, setManualRightWidth] = useState(null);

  const getResponsiveWidths = () => {
    const { isVertical } = screenSize;

    if (manualLeftWidth !== null || manualRightWidth !== null) {
      const leftWidth = manualLeftWidth || (isVertical ? 35 : 30);
      const rightWidth =
        manualRightWidth || (rightPanelVisible ? (isVertical ? 30 : 45) : 0);
      const middleWidth = 100 - leftWidth - rightWidth;
      return { leftWidth, rightWidth, middleWidth };
    }

    if (!rightPanelVisible) {
      return {
        leftWidth: isVertical ? 35 : 40,
        rightWidth: 0,
        middleWidth: isVertical ? 65 : 60,
      };
    }

    if (isVertical) {
      return { leftWidth: 45, rightWidth: 45, middleWidth: 10 };
    } else {
      return { leftWidth: 30, rightWidth: 50, middleWidth: 20 };
    }
  };

  const resetManualWidths = () => {
    setManualLeftWidth(null);
    setManualRightWidth(null);
  };

  useEffect(() => {
    resetManualWidths();
  }, [screenSize.isVertical]);

  return {
    ...getResponsiveWidths(),
    manualLeftWidth,
    manualRightWidth,
    setManualLeftWidth,
    setManualRightWidth,
    resetManualWidths,
  };
};

const ResizeHandle = ({ onMouseDown, position = "vertical" }) => {
  const style = {
    width: position === "vertical" ? "4px" : "100%",
    height: position === "horizontal" ? "4px" : "100%",
    backgroundColor: "#e5e7eb",
    cursor: position === "vertical" ? "col-resize" : "row-resize",
    position: "relative",
    transition: "background-color 0.2s",
    zIndex: 10,
  };

  return (
    <div
      style={style}
      onMouseDown={onMouseDown}
      onMouseEnter={(e) => (e.target.style.backgroundColor = "#3b82f6")}
      onMouseLeave={(e) => (e.target.style.backgroundColor = "#e5e7eb")}
      title="Drag to resize panels"
    />
  );
};

const PanelVisibilityController = ({
  hideRightPanelUntilData,
  setRightPanelVisible,
}) => {
  const { formData } = useMapForm();

  useEffect(() => {
    if (hideRightPanelUntilData) {
      const hasData = formData && formData.crops && formData.crops.length > 0;
      setRightPanelVisible(hasData);
    }
  }, [formData, hideRightPanelUntilData, setRightPanelVisible]);

  return null;
};

const IrrigationResultsDisplay = ({ irrigationResults, scenarioType }) => {
  if (!irrigationResults || !irrigationResults.powerCalcs) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          height: "300px",
          color: "#64748b",
          backgroundColor: "#f8fafc",
          borderRadius: "8px",
          border: "2px dashed #cbd5e1",
        }}
      >
        <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚡</div>
        <h3 style={{ color: "#374151", marginBottom: "8px" }}>
          No Power Calculations Available
        </h3>
        <div style={{ fontSize: "14px", textAlign: "center" }}>
          Configure irrigation system in the left panel and click
          <br />
          "Calculate Pump & Power Requirements" to see results here.
        </div>
      </div>
    );
  }

  const { powerCalcs } = irrigationResults;
  const scenarioNames = {
    standard: "Flood Irrigation System",
    drippers: "Pipe Water Course",
    scenario3: "HEIS",
    sprinklers: "HEIS System",
  };

  return (
    <div>
      {}
      <div style={{ marginBottom: "20px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "8px 12px",
            backgroundColor: "#f0f9ff",
            borderRadius: "6px",
            border: "1px solid #bae6fd",
          }}
        >
          <span style={{ fontSize: "14px", color: "#0369a1" }}>
            📋 Scenario:{" "}
            <strong>{scenarioNames[scenarioType] || scenarioType}</strong>
          </span>
          <span style={{ fontSize: "14px", color: "#0369a1" }}>
            | LPH: <strong>{irrigationResults.lph?.toFixed(2) || "N/A"}</strong>
          </span>
        </div>
      </div>

      {}
      <div
        style={{
          backgroundColor: "#f8f9fa",
          borderRadius: "6px",
          border: "1px solid #ddd",
        }}
      >
        <h4 style={{ marginTop: 0, marginBottom: "15px" }}>
          Total Dynamic Head (TDH) Summary
        </h4>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "15px",
            marginBottom: "15px",
          }}
        >
          <div
            style={{
              padding: "10px",
              backgroundColor: "#e9f7fe",
              borderRadius: "4px",
            }}
          >
            <div
              style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
            >
              Friction Losses
            </div>
            <div
              style={{ fontSize: "18px", fontWeight: "bold", color: "#2196F3" }}
            >
              {powerCalcs.frictionLosses.toFixed(2)} m
            </div>
          </div>

          <div
            style={{
              padding: "10px",
              backgroundColor: "#fff3e0",
              borderRadius: "4px",
            }}
          >
            <div
              style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
            >
              Minor Losses (20%)
            </div>
            <div
              style={{ fontSize: "18px", fontWeight: "bold", color: "#ff9800" }}
            >
              {powerCalcs.minorLosses.toFixed(2)} m
            </div>
          </div>
        </div>

        <div
          style={{
            padding: "15px",
            backgroundColor: "#fff",
            borderRadius: "4px",
            border: "2px solid #4caf50",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "14px", color: "#666", marginBottom: "4px" }}>
            Total Dynamic Head
          </div>
          <div
            style={{ fontSize: "24px", fontWeight: "bold", color: "#4caf50" }}
          >
            {powerCalcs.tdh.toFixed(2)} m
          </div>
        </div>
      </div>

      {}
      <div
        style={{
          backgroundColor: "#fff9f0",
          padding: "15px",
          borderRadius: "6px",
          border: "1px solid #ffe0b2",
        }}
      >
        <h4 style={{ marginTop: 0, marginBottom: "15px" }}>
          Power Requirements
        </h4>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "15px",
          }}
        >
          {}
          <div
            style={{
              padding: "12px",
              backgroundColor: "#fff",
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            <div
              style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
            >
              Water Horse Power (HPw)
            </div>
            <div
              style={{ fontSize: "18px", fontWeight: "bold", color: "#2196F3" }}
            >
              {powerCalcs.hpw.toFixed(1)} HP
            </div>
          </div>

          {}
          <div
            style={{
              padding: "12px",
              backgroundColor: "#fff",
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            <div
              style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
            >
              Pump Brake HP (HPp)
            </div>
            <div
              style={{ fontSize: "18px", fontWeight: "bold", color: "#2196F3" }}
            >
              {powerCalcs.hpp.toFixed(1)} HP
            </div>
          </div>

          {}
          <div
            style={{
              padding: "12px",
              backgroundColor: "#fff",
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            <div
              style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
            >
              Motor Rated HP (HPm)
            </div>
            <div
              style={{ fontSize: "18px", fontWeight: "bold", color: "#2196F3" }}
            >
              {powerCalcs.hpm.toFixed(1)} HP
            </div>
          </div>

          {}
          <div
            style={{
              padding: "12px",
              backgroundColor: "#fff",
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            <div
              style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
            >
              Solar Horse Power (HPs)
            </div>
            <div
              style={{ fontSize: "18px", fontWeight: "bold", color: "#2196F3" }}
            >
              {powerCalcs.hps.toFixed(1)} HP
            </div>
          </div>
        </div>

        {}
        <div
          style={{
            marginTop: "20px",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "15px",
          }}
        >
          <div
            style={{
              padding: "15px",
              backgroundColor: "#ffebee",
              borderRadius: "4px",
              border: "1px solid #ffcdd2",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
            >
              kW PV Output Required
            </div>
            <div
              style={{ fontSize: "20px", fontWeight: "bold", color: "#f44336" }}
            >
              {powerCalcs.kwPvOutput.toFixed(1)} kW
            </div>
          </div>

          <div
            style={{
              padding: "15px",
              backgroundColor: "#e8f5e9",
              borderRadius: "4px",
              border: "2px solid #4caf50",
              textAlign: "center",
            }}
          >
            <div
              style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}
            >
              Minimum Total Solar Capacity Required (kW)
            </div>
            <div
              style={{ fontSize: "22px", fontWeight: "bold", color: "#4caf50" }}
            >
              {powerCalcs.kwPvInput.toFixed(1)} kW
            </div>
          </div>
        </div>

        {}
        <div
          style={{
            marginTop: "15px",
            fontSize: "12px",
            color: "#666",
            textAlign: "center",
            borderTop: "1px solid #e0e0e0",
            paddingTop: "10px",
          }}
        >
          Calculated on:{" "}
          {new Date(irrigationResults.timestamp).toLocaleString()}
        </div>
      </div>
    </div>
  );
};

const TabbedChartsSection = ({ formData, formKey }) => {
  const { irrigationResults, irrigationScenarioType } = useMapForm();
  const [activeTab, setActiveTab] = useState("water-analysis");

  useEffect(() => {
    if (irrigationResults && irrigationResults.powerCalcs) {
      setActiveTab("irrigation-results");
    }
  }, [irrigationResults]);

  const tabStyle = (isActive) => ({
    padding: "10px 16px",
    border: "none",
    backgroundColor: isActive ? "#3b82f6" : "#f8f9fa",
    color: isActive ? "white" : "#64748b",
    cursor: "pointer",
    borderRadius: "6px 6px 0 0",
    fontSize: "14px",
    fontWeight: isActive ? "600" : "400",
    marginRight: "2px",
    transition: "all 0.2s ease",
  });

  const tabContent = {
    backgroundColor: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "0 8px 8px 8px",
    padding: "5px",
    minHeight: "400px",
  };

  return (
    <div>
      <div style={{ display: "flex", marginBottom: "0" }}>
        <button
          style={tabStyle(activeTab === "water-analysis")}
          onClick={() => setActiveTab("water-analysis")}
        >
          💧 Water Requirements
        </button>
        <button
          style={tabStyle(activeTab === "crop-coefficients")}
          onClick={() => setActiveTab("crop-coefficients")}
        >
          🌱 Crop Stages
        </button>
        {irrigationResults && (
          <button
            style={tabStyle(activeTab === "irrigation-results")}
            onClick={() => setActiveTab("irrigation-results")}
          >
            ⚡ Power Calculations
          </button>
        )}
      </div>

      <div style={tabContent}>
        {activeTab === "water-analysis" && (
          <ThreeChartWaterDisplay
            key={`three-charts-${formKey}`}
            combinedData={formData}
          />
        )}

        {activeTab === "crop-coefficients" && (
          <CombinedCropCoefficient
            key={`crop-coeff-${formKey}`}
            combinedData={formData}
          />
        )}

        {activeTab === "irrigation-results" && irrigationResults && (
          <IrrigationResultsDisplay
            irrigationResults={irrigationResults}
            scenarioType={irrigationScenarioType}
          />
        )}
      </div>
    </div>
  );
};

const LeftPanel = () => {
  const {
    latitude,
    longitude,
    area,
    crop_type,
    planting_date,
    mapActions,
    handleLocationChange,
    handleCropInfoChange,
    handleDataReceived,
    handleIrrigationResults,
    formData,

    detectedProvince,
    selectedProvince,
    getEffectiveProvince,
  } = useMapForm();

  const [isFormCollapsed, setIsFormCollapsed] = useState(false);
  const [isIrrigationCollapsed, setIsIrrigationCollapsed] = useState(false);

  useEffect(() => {
    const hasFormData = formData && formData.crops && formData.crops.length > 0;
    setIsFormCollapsed(hasFormData);
  }, [formData]);

  const styles = {
    panelHeader: {
      padding: "5px",
      borderBottom: "1px solid #e5e7eb",
      fontWeight: "500",
      color: "#1f2937",
      fontSize: "18px",
    },
    panelContent: {
      flex: 1,
      overflow: "auto",
    },
    collapsibleHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 16px",
      backgroundColor: "#f8fafc",
      borderBottom: "1px solid #e2e8f0",
      cursor: "pointer",
      userSelect: "none",
    },
    collapsibleContent: {
      display: isFormCollapsed ? "none" : "block",
    },
    irrigationSection: {
      marginTop: "10px",
      backgroundColor: "#f8fafc",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
    },

    provinceSection: {
      marginBottom: "20px",
      padding: "16px",
      backgroundColor: "#f0f9ff",
      borderRadius: "8px",
      border: "1px solid #bae6fd",
    },
    provinceLabel: {
      display: "block",
      marginBottom: "8px",
      fontWeight: "500",
      color: "#0369a1",
      fontSize: "14px",
    },
    provinceSelect: {
      width: "100%",
      padding: "8px 12px",
      borderRadius: "6px",
      border: "1px solid #bae6fd",
      backgroundColor: "white",
      fontSize: "14px",
      color: "#374151",
    },
    provinceStatus: {
      fontSize: "12px",
      color: "#0369a1",
      marginTop: "6px",
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    provinceIcon: {
      fontSize: "16px",
    },
  };

  const effectiveProvince = getEffectiveProvince();

  return (
    <>
      <div style={styles.panelHeader}>
        <span>🌾 Crop Configuration</span>
        {effectiveProvince && (
          <span
            style={{ fontSize: "12px", color: "#6b7280", marginLeft: "8px" }}
          >
            📍 {effectiveProvince}
          </span>
        )}
      </div>

      <div style={styles.panelContent}>
        {}
        <div>
          {formData && formData.crops && formData.crops.length > 0 && (
            <div
              style={styles.collapsibleHeader}
              onClick={() => setIsFormCollapsed(!isFormCollapsed)}
            >
              <span style={{ fontWeight: "500", color: "#374151" }}>
                🌾 Cropping System Details{" "}
                {}
              </span>
              <button
                style={{ background: "none", border: "none", fontSize: "16px" }}
              >
                {isFormCollapsed ? "▼" : "▲"}
              </button>
            </div>
          )}

          <div style={styles.collapsibleContent}>
            <MultiCropFormComponent
              latitude={latitude}
              longitude={longitude}
              area={area}
              crop_type={crop_type}
              planting_date={planting_date}

              detectedProvince={detectedProvince}
              selectedProvince={selectedProvince}
              onLocationChange={handleLocationChange}
              onCropInfoChange={handleCropInfoChange}
              onDataReceived={handleDataReceived}
              mapActions={mapActions}
              showLocationInputs={true}
              showChartsSection={false}
            />
          </div>
        </div>

        {}
        {formData && formData.crops && formData.crops.length > 0 && (
          <div style={styles.irrigationSection}>
            <div
              style={{
                ...styles.collapsibleHeader,
                backgroundColor: "#f0f9ff",
                borderRadius: "8px 8px 0 0",
              }}
              onClick={() => setIsIrrigationCollapsed(!isIrrigationCollapsed)}
            >
              <span style={{ fontWeight: "500", color: "#374151" }}>
                💧 Irrigation System Configuration{" "}
                {isIrrigationCollapsed ? "(Collapsed)" : ""}
              </span>
              <button
                style={{ background: "none", border: "none", fontSize: "16px" }}
              >
                {isIrrigationCollapsed ? "▼" : "▲"}
              </button>
            </div>

            {!isIrrigationCollapsed && (
              <IrrigationScenarioSelector
                combinedData={formData}
                cropData={
                  formData.crops.length === 1
                    ? formData.crops[0].crop_data
                    : null
                }
                growthStageData={
                  formData.crops.length === 1
                    ? formData.crops[0].growth_stage_calculations
                    : null
                }
                plantingDate={
                  formData.crops.length === 1
                    ? formData.crops[0].crop_input.planting_date
                    : null
                }
                areaInAcres={formData.crops.reduce(
                  (total, crop) => total + (parseFloat(crop.crop_input?.area) || 0),
                  0
                )}
                onScenarioDataChange={(scenarioType, data) => {}}
                onIrrigationResults={handleIrrigationResults}
              />
            )}
          </div>
        )}

        {}
        {isFormCollapsed && (
          <div
            style={{
              marginTop: "16px",
              padding: "12px",
              backgroundColor: "#f0f9ff",
              borderRadius: "6px",
              border: "1px solid #bae6fd",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                color: "#0369a1",
                marginBottom: "8px",
              }}
            >
              📊 {formData?.total_crops || 0} crops configured
              {effectiveProvince && (
                <span style={{ marginLeft: "8px" }}>
                  📍 {effectiveProvince}
                </span>
              )}
            </div>
            <button
              onClick={() => setIsFormCollapsed(false)}
              style={{
                background: "#3b82f6",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: "4px",
                fontSize: "14px",
                cursor: "pointer",
                marginRight: "8px",
              }}
            >
              Edit Crops
            </button>
            <button
              onClick={() => {
                handleDataReceived(null);
                setIsFormCollapsed(false);
              }}
              style={{
                background: "#6b7280",
                color: "white",
                border: "none",
                padding: "8px 16px",
                borderRadius: "4px",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              Start Over
            </button>
          </div>
        )}
      </div>
    </>
  );
};

const MainPanel = () => {
  const { latitude, longitude, handleLocationChange, handleMapReady } =
    useMapForm();

  return (
    <div style={{ flex: 1, padding: "16px", overflow: "auto" }}>
      <MapComponent
        latitude={latitude}
        longitude={longitude}
        onLocationChange={handleLocationChange}
        onMapReady={handleMapReady}
      />
    </div>
  );
};

const RightPanel = () => {
  const { formData, isLoading, error, formKey } = useMapForm();

  const styles = {
    panelHeader: {
      padding: "var(--space-4) var(--space-5)",
      borderBottom: `1px solid var(--color-neutral-200)`,
      fontWeight: "var(--font-weight-semibold)",
      color: "var(--color-neutral-900)",
      fontSize: "var(--font-size-base)",
      background: "var(--color-neutral-50)",
      display: "flex",
      alignItems: "center",
      gap: "var(--space-2)",
    },
    panelContent: {
      flex: 1,
      padding: "var(--space-5)",
      overflow: "auto",
      background: "#ffffff",
    },
  };

  return (
    <>
      <div style={styles.panelHeader}>Analysis Results</div>
      <div style={styles.panelContent}>
        {isLoading && (
          <div
            style={{ textAlign: "center", padding: "20px", color: "#6b7280" }}
          >
            Loading...
          </div>
        )}

        {error && (
          <div
            style={{
              color: "#ef4444",
              padding: "16px",
              backgroundColor: "#fef2f2",
              borderRadius: "8px",
              border: "1px solid #fecaca",
              marginBottom: "16px",
            }}
          >
            {error}
          </div>
        )}

        {formData && formData.crops && formData.crops.length > 0 ? (
          <div>
            <CropSummaryCards combinedData={formData} />
            <div
              style={{
                borderTop: "2px solid #e2e8f0",
                marginTop: "16px",
                paddingTop: "16px",
              }}
            >
              <TabbedChartsSection formData={formData} formKey={formKey} />
            </div>
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              color: "#64748b",
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              border: "2px dashed #cbd5e1",
            }}
          >
            <div style={{ fontSize: "18px", marginBottom: "8px" }}>
              📊 No Analysis Data
            </div>
            <div style={{ fontSize: "14px" }}>
              Configure and submit crops in the left panel to see analysis
              results and charts here.
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export const FlexibleLayoutCSS = ({
  initialMapData = {},
  hideRightPanelUntilData = true,
}) => {
  const [headerExpanded, setHeaderExpanded] = useState(true);
  const [rightPanelVisible, setRightPanelVisible] = useState(
    !hideRightPanelUntilData
  );

  const screenSize = useResponsive();
  const {
    leftWidth,
    rightWidth,
    middleWidth,
    setManualLeftWidth,
    setManualRightWidth,
  } = useResizableLayout(screenSize, rightPanelVisible);

  const handleLeftResize = (e) => {
    const startX = e.clientX;
    const startWidth = leftWidth;

    const handleMouseMove = (e) => {
      const deltaX = e.clientX - startX;
      const containerWidth = window.innerWidth;
      const deltaPercent = (deltaX / containerWidth) * 100;

      const minWidth = screenSize.isVertical ? 20 : 15;
      const maxWidth = screenSize.isVertical ? 50 : 60;

      const newWidth = Math.max(
        minWidth,
        Math.min(maxWidth, startWidth + deltaPercent)
      );

      setManualLeftWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleRightResize = (e) => {
    const startX = e.clientX;
    const startWidth = rightWidth;

    const handleMouseMove = (e) => {
      const deltaX = startX - e.clientX;
      const containerWidth = window.innerWidth;
      const deltaPercent = (deltaX / containerWidth) * 100;

      const minWidth = screenSize.isVertical ? 25 : 20;
      const maxWidth = screenSize.isVertical ? 55 : 60;

      const newWidth = Math.max(
        minWidth,
        Math.min(maxWidth, startWidth + deltaPercent)
      );

      setManualRightWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const styles = {
    container: {
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      backgroundColor: "var(--color-neutral-50)",
      fontFamily: "var(--font-family-sans)",
    },
    header: {
      height: headerExpanded ? "var(--header-height)" : "3rem",
      backgroundColor: "var(--card-background)",
      borderBottom: `1px solid var(--color-neutral-200)`,
      transition: "height var(--transition-slow)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0 var(--space-4)",
      boxShadow: "var(--shadow-sm)",
      zIndex: "var(--z-sticky)",
    },
    headerTitle: {
      fontSize: "var(--font-size-xl)",
      fontWeight: "var(--font-weight-semibold)",
      color: "var(--color-primary-700)",
      letterSpacing: "-0.01em",
      fontFamily: "var(--font-family-serif)",
    },
    toggleButton: {
      padding: "var(--space-2)",
      border: "none",
      borderRadius: "var(--radius-md)",
      backgroundColor: "transparent",
      cursor: "pointer",
      transition: "all var(--transition-base)",
      color: "var(--color-neutral-600)",
    },
    mainContent: {
      flex: 1,
      display: "flex",
      overflow: "hidden",
      gap: "1px",
      backgroundColor: "var(--color-neutral-200)",
    },
    leftColumn: {
      width: `${leftWidth}%`,
      backgroundColor: "#ffffff",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxShadow: "var(--shadow-sm)",
    },
    middleColumn: {
      width: `${middleWidth}%`,
      backgroundColor: "var(--color-neutral-100)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    rightColumn: {
      width: `${rightWidth}%`,
      backgroundColor: "#ffffff",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxShadow: "var(--shadow-sm)",
    },
  };

  return (
    <MapFormProvider initialData={initialMapData}>
      <PanelVisibilityController
        hideRightPanelUntilData={hideRightPanelUntilData}
        setRightPanelVisible={setRightPanelVisible}
      />

      <div style={styles.container}>
        <Header />

        <div style={styles.mainContent}>
          <div style={styles.leftColumn}>
            <LeftPanel />
          </div>

          <ResizeHandle onMouseDown={handleLeftResize} />

          <div style={styles.middleColumn}>
            <MainPanel />
          </div>

          {rightPanelVisible && (
            <>
              <ResizeHandle onMouseDown={handleRightResize} />
              <div style={styles.rightColumn}>
                <RightPanel />
              </div>
            </>
          )}
        </div>
      </div>
    </MapFormProvider>
  );
};
